<template>
  <UForm
    ref="form"
    :schema="schema"
    :state="state"
    class="space-y-3"
    @submit="submitForm"
  >
    <TGrid :cols="12">
      <UFormField
        class="grid-rows-1 col-span-12 md:col-span-4 mt-5 md:mt-0"
        :label="t('views.cottage-contact.form.firstName.label')"
        required
        name="name"
      >
        <UInput
          v-model="state.name"
          :aria-label="t('views.cottage-contact.form.firstName.label')"
          :title="t('views.cottage-contact.form.firstName.title')"
          size="lg"
          autocomplete="on"
        />
      </UFormField>
      <UFormField
        class="grid-rows-1 col-span-12 md:col-span-4 mt-5 md:mt-0"
        :label="t('views.cottage-contact.form.email.label')"
        required
        name="email"
      >
        <UInput
          v-model="state.email"
          :aria-label="t('views.cottage-contact.form.email.label')"
          :title="t('views.cottage-contact.form.email.title')"
          type="text"
          inputmode="email"
          size="lg"
          autocomplete="on"
        />
      </UFormField>
      <UFormField
        class="grid-rows-1 col-span-12 md:col-span-4 mt-5 md:mt-0"
        :label="t('views.cottage-contact.form.phone.label')"
        required
        name="phone"
      >
        <UInput
          v-model="state.phone"
          :aria-label="t('views.cottage-contact.form.phone.label')"
          :title="t('views.cottage-contact.form.phone.title')"
          size="lg"
          type="tel"
          autocomplete="on"
        />
      </UFormField>
      <UFormField
        class="grid-rows-1 col-span-12 mt-5 md:mt-8"
        :label="t('views.cottage-contact.form.message.label')"
        required
        name="message"
      >
        <UTextarea
          v-model="state.message"
          :aria-label="t('views.cottage-contact.form.message.label')"
          :title="t('views.cottage-contact.form.message.title')"
          autoresize
          size="xl"
          :maxlength="MAX_CHARACTERS"
        />
        <TText color="text-gray-600" size="sm" classes="mt-2">
          {{ t('views.cottage-contact.form.message.max', remainingCharacters) }}
        </TText>
      </UFormField>
      <UFormField
        class="grid-rows-1 col-span-12 mt-3 md:mt-5"
        name="allowComms"
      >
        <UCheckbox
          v-model="state.allowComms"
          class="mt-5"
          :label="`${t('views.cottage-contact.form.comms.label')}.`"
        />
      </UFormField>
      <UFormField
        class="grid-rows-1 col-span-12 mt-3 md:mt-5"
        name="privacyPolicy"
      >
        <UCheckbox v-model="state.privacyPolicy" required>
          <template #label>
            <i18n-t
              keypath="views.cottage-contact.form.confirmation.label"
              tag="span"
              scope="global"
            >
              <template #useConditions>
                <ULink
                  :active="true"
                  target="_blank"
                  :href="routes().useConditions()"
                  >{{
                    t('views.cottage-contact.form.confirmation.useConditions')
                  }}</ULink
                >
              </template>
              <template #privacyPolicy>
                <ULink
                  :active="true"
                  target="_blank"
                  :href="routes().privacyPolicy()"
                  >{{
                    t('views.cottage-contact.form.confirmation.privacyPolicy')
                  }}</ULink
                >
              </template>
            </i18n-t>
          </template>
        </UCheckbox>
      </UFormField>
      <div class="grid-rows-1 col-span-12">
        <UButton
          :ui="mobileFullWidthButton"
          class="mt-6"
          size="lg"
          type="submit"
          :disabled="!isValid"
        >
          {{ t('views.cottage-contact.form.send') }}
        </UButton>
      </div>
    </TGrid>
  </UForm>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '#ui/types'
import { z } from 'zod'
import { travelerContactParams } from '~~/shared/api/rurality/v3/contacts/new/schema'
import { useZodValidator } from '~~/modules/form/composables/useZodValidator'
import type { CottageContactPageCreateForm } from '../entities/CottageContactPageCreate'
import type { CottageContactPage } from '../entities/CottageContactPage'
import { mobileFullWidthButton } from '~~/modules/design-system/utils/nuxt-ui-config/components/button'

// Composables
const { getCookieRuralityCottageContact } = useCookiesRurality()

const { validateEmail, validatePhone, validateRequiredCheckbox } =
  useZodValidator()

// Props
type itemType = Pick<CottageContactPage, 'id'>
interface propsInterface {
  item: itemType
}
defineProps<propsInterface>()

// Const
const MAX_CHARACTERS = 2000
const remainingCharacters = ref(MAX_CHARACTERS)

const state = reactive({
  name: '',
  email: '',
  phone: '',
  message: '',
  allowComms: false,
  privacyPolicy: false,
})

const schema = travelerContactParams.extend({
  name: z.string().min(2).max(30),
  email: validateEmail(),
  phone: validatePhone(),
  message: z.string().min(1).max(MAX_CHARACTERS),
  privacyPolicy: validateRequiredCheckbox(),
})

const isValid = computed(() => {
  return schema.safeParse(state).success
})

// METHODS
const setCookieData = () => {
  const initialData = getCookieRuralityCottageContact()

  if (initialData) {
    state.name = initialData.traveler_name
    state.email = initialData.traveler_email
    state.phone = initialData.traveler_phone
    state.message = initialData.message
  }
}

async function submitForm(event: FormSubmitEvent<Record<string, unknown>>) {
  const result = schema.safeParse(event.data)

  if (result.success) {
    const formData = result.data
    const parsedData: CottageContactPageCreateForm = {
      name: formData.name as string,
      email: formData.email as string,
      phone: formData.phone as string,
      allowComms: formData.allowComms,
      message: '',
    }

    const finalMessage = state.message

    parsedData.message = finalMessage

    emit('submit', parsedData)
  }
}

const emit = defineEmits<{
  (
    e: 'submit',
    cottageContactPageCreateForm: CottageContactPageCreateForm,
  ): void
}>()

onMounted(() => {
  setCookieData()
})

watch(
  () => state.message,
  newMessage => {
    remainingCharacters.value = Math.max(
      0,
      MAX_CHARACTERS - (newMessage?.length || 0),
    )
  },
  { immediate: true },
)
</script>
